import { useState, useEffect } from "react";
import {
  setRSVP,
  getOccurrenceStatus,
  formatOccurrenceTime,
  dateKeyFromDate,
} from "./recurringCalls";

const card = {
  background: "#f5faff",
  borderRadius: "22px",
  padding: "20px",
  border: "2px solid #dbeafe",
  boxShadow: "0 8px 20px rgba(30,58,138,0.10)",
  marginBottom: "16px",
};

function useCountdown(targetTime) {
  const [label, setLabel] = useState(null);
  useEffect(() => {
    if (!targetTime) return;
    const t = targetTime?.toDate ? targetTime.toDate() : new Date(targetTime);
    const tick = () => {
      const diff = t - new Date();
      if (diff <= 0) { setLabel(null); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setLabel(h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetTime]);
  return label;
}

export default function RecurringCallCard({ occurrence, pollData, currentUser, compact = false }) {
  const [rsvpSaving, setRsvpSaving] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [displayName, setDisplayName] = useState(currentUser?.name || "");
  const [showNamePrompt, setShowNamePrompt] = useState(false);

  const scheduledTime = occurrence?.scheduledTime ||
    (pollData?.scheduledTime) ||
    null;

  const status = pollData?.status === "cancelled"
    ? "cancelled"
    : getOccurrenceStatus(scheduledTime);

  const countdown = useCountdown(status === "upcoming" || status === "live" ? scheduledTime : null);
  const myRSVP = pollData?.rsvps?.[currentUser?.uid];
  const rsvps = pollData?.rsvps || {};
  const yesCount = Object.values(rsvps).filter((v) => v === "yes").length;
  const noCount = Object.values(rsvps).filter((v) => v === "no").length;
  const dateKey = occurrence?.dateKey || (scheduledTime
    ? dateKeyFromDate(scheduledTime?.toDate ? scheduledTime.toDate() : new Date(scheduledTime))
    : null);

  const title = occurrence?.title || "Weekly Group Call";
  const note = pollData?.note || occurrence?.note || "";
  const roomUrl = pollData?.roomUrl;

  async function handleRSVP(response) {
    if (!dateKey || !currentUser?.uid) return;
    setRsvpSaving(true);
    try {
      await setRSVP(dateKey, currentUser.uid, myRSVP === response ? null : response);
    } finally {
      setRsvpSaving(false);
    }
  }

  function handleJoinClick() {
    setDisplayName(currentUser?.name || "");
    setShowNamePrompt(true);
  }

  // Full-screen call
  if (inCall && roomUrl) {
    const src = roomUrl + (displayName ? "?name=" + encodeURIComponent(displayName) : "");
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "#000", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px", background: "#0f172a" }}>
          <span style={{ color: "#fff", fontWeight: "bold", fontSize: "15px" }}>📹 {title}</span>
          <button
            onClick={() => setInCall(false)}
            style={{ padding: "6px 14px", borderRadius: "10px", background: "#475569", color: "#fff", border: "none", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
          >
            Leave
          </button>
        </div>
        <iframe
          src={src}
          allow="camera; microphone; screen-capture; fullscreen; display-capture"
          style={{ flex: 1, border: "none", width: "100%" }}
          title={title}
        />
      </div>
    );
  }

  // Name prompt
  if (showNamePrompt) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
        <div style={{ background: "#fff", borderRadius: "20px", padding: "28px 24px", width: "100%", maxWidth: "360px", boxShadow: "0 8px 32px rgba(0,0,0,0.25)" }}>
          <div style={{ fontWeight: "bold", fontSize: "18px", color: "#1e3a5f", marginBottom: "6px" }}>Join Call</div>
          <div style={{ fontSize: "13px", color: "#4b6b8a", marginBottom: "18px" }}>Your name will be visible to everyone in the call.</div>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your name"
            autoFocus
            style={{ width: "100%", padding: "12px 14px", borderRadius: "12px", border: "2px solid #bfdbfe", fontSize: "15px", color: "#1e3a5f", outline: "none", boxSizing: "border-box", marginBottom: "16px" }}
          />
          <button
            onClick={() => { setShowNamePrompt(false); setInCall(true); }}
            disabled={!displayName.trim()}
            style={{ width: "100%", padding: "13px", borderRadius: "13px", background: displayName.trim() ? "#2563eb" : "#bfdbfe", color: "#fff", border: "none", fontWeight: "bold", fontSize: "16px", cursor: displayName.trim() ? "pointer" : "default", marginBottom: "10px" }}
          >
            Join Now →
          </button>
          <button
            onClick={() => setShowNamePrompt(false)}
            style={{ width: "100%", padding: "10px", borderRadius: "12px", background: "transparent", color: "#64748b", border: "2px solid #e2e8f0", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (!occurrence && !scheduledTime) return null;

  const timeLabel = scheduledTime ? formatOccurrenceTime(scheduledTime) : "";

  // Compact dashboard banner
  if (compact) {
    return (
      <div style={{ background: status === "live" ? "#ecfdf5" : "#f0f9ff", borderRadius: "16px", padding: "14px 18px", border: status === "live" ? "2px solid #6ee7b7" : "2px solid #bae6fd", marginBottom: "14px", display: "flex", alignItems: "center", gap: "12px" }}>
        <div style={{ fontSize: "26px" }}>{status === "live" ? "🟢" : "📹"}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>
            {status === "live" ? "Call is live now!" : title}
          </div>
          <div style={{ fontSize: "12px", color: "#4b6b8a", marginTop: "2px" }}>
            {status === "live" ? "Tap to join" : timeLabel}
          </div>
        </div>
        {status === "live" && roomUrl ? (
          <button
            onClick={handleJoinClick}
            style={{ padding: "8px 16px", borderRadius: "10px", background: "#059669", color: "#fff", border: "none", fontWeight: "bold", fontSize: "13px", cursor: "pointer", whiteSpace: "nowrap" }}
          >
            Join →
          </button>
        ) : (
          <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
            <button
              onClick={() => handleRSVP("yes")}
              disabled={rsvpSaving}
              style={{ padding: "7px 12px", borderRadius: "9px", border: myRSVP === "yes" ? "2px solid #059669" : "2px solid #bfdbfe", background: myRSVP === "yes" ? "#dcfce7" : "#fff", color: myRSVP === "yes" ? "#059669" : "#4b6b8a", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
            >
              ✓ Going
            </button>
            <button
              onClick={() => handleRSVP("no")}
              disabled={rsvpSaving}
              style={{ padding: "7px 12px", borderRadius: "9px", border: myRSVP === "no" ? "2px solid #dc2626" : "2px solid #bfdbfe", background: myRSVP === "no" ? "#fee2e2" : "#fff", color: myRSVP === "no" ? "#dc2626" : "#4b6b8a", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
            >
              ✗ Can't
            </button>
          </div>
        )}
      </div>
    );
  }

  // Full card (Calls tab)
  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
        <span style={{ fontSize: "28px" }}>{status === "live" ? "🟢" : status === "cancelled" ? "🚫" : "📅"}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "17px" }}>{title}</div>
          {status === "cancelled" ? (
            <div style={{ fontSize: "13px", color: "#dc2626", marginTop: "2px" }}>
              Cancelled{note ? ` — ${note}` : ""}
            </div>
          ) : (
            <div style={{ fontSize: "13px", color: "#2563eb", fontWeight: "bold", marginTop: "2px" }}>
              {status === "live" ? "Happening now!" : timeLabel}
            </div>
          )}
        </div>
      </div>

      {status === "upcoming" && (
        <>
          {note && (
            <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: "10px", padding: "8px 12px", fontSize: "12px", color: "#92400e", marginBottom: "12px" }}>
              📌 {note}
            </div>
          )}

          {countdown && (
            <div style={{ fontSize: "13px", color: "#4b6b8a", marginBottom: "10px" }}>
              Starts in <strong>{countdown}</strong>
            </div>
          )}

          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "13px", color: "#1e3a5f", fontWeight: "bold", marginBottom: "10px" }}>
              Can you make it?
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => handleRSVP("yes")}
                disabled={rsvpSaving}
                style={{ flex: 1, padding: "12px", borderRadius: "12px", border: myRSVP === "yes" ? "2px solid #059669" : "2px solid #dbeafe", background: myRSVP === "yes" ? "#dcfce7" : "#f8fbff", color: myRSVP === "yes" ? "#059669" : "#1e3a5f", fontWeight: "bold", fontSize: "15px", cursor: "pointer" }}
              >
                ✓ I'll be there
              </button>
              <button
                onClick={() => handleRSVP("no")}
                disabled={rsvpSaving}
                style={{ flex: 1, padding: "12px", borderRadius: "12px", border: myRSVP === "no" ? "2px solid #dc2626" : "2px solid #dbeafe", background: myRSVP === "no" ? "#fee2e2" : "#f8fbff", color: myRSVP === "no" ? "#dc2626" : "#1e3a5f", fontWeight: "bold", fontSize: "15px", cursor: "pointer" }}
              >
                ✗ Can't make it
              </button>
            </div>
          </div>

          {(yesCount > 0 || noCount > 0) && (
            <div style={{ fontSize: "12px", color: "#4b6b8a" }}>
              {yesCount > 0 && <span style={{ marginRight: "10px" }}>✓ {yesCount} going</span>}
              {noCount > 0 && <span>✗ {noCount} can't make it</span>}
            </div>
          )}

          {!roomUrl && (
            <div style={{ marginTop: "12px", background: "#eff6ff", borderRadius: "12px", padding: "10px 14px", border: "1px solid #bfdbfe", fontSize: "12px", color: "#1e40af" }}>
              Join link will appear here when your coach opens the room.
            </div>
          )}
        </>
      )}

      {status === "live" && roomUrl && (
        <button
          onClick={handleJoinClick}
          style={{ width: "100%", padding: "14px", borderRadius: "14px", background: "#059669", color: "#fff", border: "none", fontWeight: "bold", fontSize: "17px", cursor: "pointer", boxShadow: "0 4px 14px rgba(5,150,105,0.35)" }}
        >
          Join Now →
        </button>
      )}

      {status === "live" && !roomUrl && (
        <div style={{ background: "#fef3c7", borderRadius: "12px", padding: "12px", fontSize: "13px", color: "#92400e", textAlign: "center" }}>
          Call is starting — join link coming shortly!
        </div>
      )}
    </div>
  );
}
